export function formatDateTime(value) {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString(undefined, {
    day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit',
  });
}

export function timeAgo(value, now = Date.now()) {
  if (!value) return '';
  const diff = Math.max(0, now - new Date(value).getTime());
  const min = Math.floor(diff / 60000);
  if (min < 1) return 'just now';
  if (min < 60) return `${min} min ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr} h ago`;
  return `${Math.floor(hr / 24)} d ago`;
}

/** Plain-language reason a run did not complete normally. */
export function stopReasonText(reason) {
  const map = {
    steps: 'The run reached its step limit, so it stopped early. Partial results are shown.',
    tokens: 'The run reached its token limit, so it stopped early. Partial results are shown.',
    time: 'The run reached its time limit, so it stopped early. Partial results are shown.',
    no_ai_key: 'No working AI key is connected. Add or fix your AI key in Connections, then try again.',
    no_orka_connection: 'Your Orka store is not connected. Add the connector token in Connections, then try again.',
    provider_unreachable: 'The AI provider could not be reached. Try again in a moment.',
    error: 'Something went wrong while running. Try again, and contact support if it keeps happening.',
  };
  return map[reason] || '';
}

const ERROR_TEXT = {
  invalid_credentials: 'Incorrect mobile, email or password.',
  rate_limited: 'Too many attempts. Please wait a few minutes and try again.',
  owner_required: 'Only the account owner can do this.',
  no_organization: 'Your account is not linked to an Orka organization yet. Create a store in Orka first.',
  store_required: 'This account has more than one store. Choose which store to connect.',
  no_store: 'No store was found for that key.',
  mcp_not_configured: 'Store connection is not set up on the server yet. Contact support.',
  invalid_analytics_key: 'That analytics key was rejected. Check it and try again.',
  analytics_account_mismatch: 'That analytics key belongs to a different account. Use a key from the account you are signed in with.',
  analytics_unreachable: 'Could not reach analytics to check the key. Try again shortly.',
  invalid_title: 'Give it a title of 2 to 200 characters.',
  invalid_due_date: 'Choose a valid due date.',
  invalid_assignee: 'Choose an active team member.',
  task_limit: 'There are too many open tasks. Finish some first.',
  schedule_limit: 'You can have up to 10 schedules.',
  trigger_limit: 'You can have up to 10 triggers.',
  app_not_connected: 'Connect that app first (Connections), then create the trigger.',
  invalid_event: 'Choose what to watch for.',
  invalid_threshold: 'The stock level must be a whole number from 0 to 1000.',
  invalid_cooldown: 'Wait time must be between 5 and 1440 minutes.',
  invalid_time: 'Choose a valid time.',
  invalid_timezone: 'That timezone is not recognised.',
  invalid_conversation: 'Choose a chat.',
  daily_limit: 'The daily limit for scheduled runs is reached.',
  whatsapp_not_connected: 'Connect WhatsApp first (Connections), then switch this on.',
  invalid_whatsapp_key: 'That WhatsApp key was rejected. Check it and try again.',
  no_whatsapp: 'No WhatsApp number is connected for that key.',
  whatsapp_org_mismatch: 'That WhatsApp key belongs to a different organization.',
  whatsapp_unreachable: 'Could not reach WhatsApp to check the key. Try again shortly.',
  name_taken: 'A team member with that name already exists.',
  primary_locked: 'OrkaCrew is managed by the platform and cannot be changed.',
  reporting_loop: 'That would make the reporting lines loop.',
  invalid_permissions: 'That access is not available.',
  invalid_manager: 'Choose a valid manager.',
  approval_failed: 'The request was approved but could not be completed.',
  run_in_progress: 'A run is already in progress. Wait for it to finish, then try again.',
  invalid_ai_key: 'That AI key was rejected. Check it and try again.',
  provider_unreachable: 'Could not reach the AI provider to check the key. Try again shortly.',
  invalid_orka_token: 'That connector token was rejected. Create a new one in your store and paste it again.',
  orka_org_mismatch: 'That token belongs to a different Orka organization.',
  orka_unreachable: 'Could not reach your Orka store to check the token. Try again shortly.',
  invalid_otp: 'That code is incorrect or has expired.',
  mobile_exists: 'This mobile number already has an account. Sign in instead.',
  approval_not_pending: 'This request was already handled.',
  approval_expired: 'This request has expired.',
};

/** Friendly message for any thrown error. */
export function errorText(err) {
  if (!err) return '';
  return ERROR_TEXT[err.code] || err.message || 'Something went wrong.';
}

export function isValidMobile(value) {
  return /^\d{10,15}$/.test(String(value || '').trim());
}

export function isEmail(value) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value || '').trim());
}

export function passwordProblem(password) {
  if (!password || password.length < 8) return 'Use at least 8 characters.';
  if (!/[a-z]/.test(password)) return 'Include a lowercase letter.';
  if (!/[A-Z]/.test(password)) return 'Include an uppercase letter.';
  if (!/[0-9]/.test(password)) return 'Include a number.';
  return '';
}
